import { Component, inject, Input, OnInit } from '@angular/core';
import { catchError, forkJoin, map, of, take, tap } from 'rxjs';
import { BookingService, CartridgesFacade } from '@markant-platform/cartridge/data-access';
import { BookingData, BookingState } from '@markant-platform/cartridge/model';
import { daysLeft } from '@markant-platform/shared/util-helpers';
import { NgClass } from '@angular/common';
import { TranslocoPipe } from '@jsverse/transloco';
import { OrganizationCompleteProfileButtonComponent } from '@markant-platform/shared/organization/feature';
import { OrganizationService } from '@markant-platform/shared/organization/data-access';
import { AuthenticatedUserService } from '@markant-platform/shared/user/data-access';
import { StaticRole } from '@markant-platform/shared/user/model';
import { MarkantElementsModule } from '@markant/elements-angular';
import { Router } from '@angular/router';
import { QualificationLevel } from 'data-model';

export type ChipColorScheme = 'secondary' | 'warning' | 'neutral';

@Component({
  selector: 'markant-platform-widget-demo-chip',
  templateUrl: './widget-demo-chip.component.html',
  styleUrls: ['./widget-demo-chip.component.scss'],
  standalone: true,
  imports: [NgClass, TranslocoPipe, OrganizationCompleteProfileButtonComponent, MarkantElementsModule],
})
export class WidgetDemoChipComponent implements OnInit {
  private readonly cartridgesFacade = inject(CartridgesFacade);
  private readonly organizationService = inject(OrganizationService);
  private readonly authenticatedUserService = inject(AuthenticatedUserService);
  private readonly router: Router = inject(Router);
  private readonly bookingService: BookingService = inject(BookingService);

  @Input() variantId?: string;
  now = new Date();
  statusDemo: ChipColorScheme = 'secondary';
  daysRemaining = 0;
  labelTranslationKey = '';
  isFurtherQualificationEligible = false;
  isFurtherQualificationProcessInProgress = false;
  isOrgAdmin = false;
  cartridgeId: string | undefined;
  variantStatus: BookingState = BookingState.ENDED;
  variantStatusEnded = [BookingState.ENDED, BookingState.PENDING, BookingState.DECLINED];
  validStatus = [
    BookingState.BOOKED,
    BookingState.ACCEPTED,
    BookingState.PENDING,
    BookingState.TO_BE_CANCELLED,
    BookingState.ENDED,
  ];

  getChipLabelTranslationKey(daysLeft: number, status: BookingState): string {
    if (daysLeft < 0 || this.variantStatusEnded.includes(status)) {
      return 'catalog.detail.cartridgeVariants.demo.ended';
    } else {
      return 'catalog.detail.cartridgeVariants.demo.daysLeft';
    }
  }

  ngOnInit(): void {
    if (this.variantId) {
      const roles$ = this.authenticatedUserService.getUserRoles().pipe(
        map(roles => roles.map(({ name }) => name as StaticRole)),
        tap(roles => {
          this.isOrgAdmin = roles.includes(StaticRole.ORGANIZATION_ADMIN);
        })
      );
      const organization$ = this.organizationService.fetchOrganization().pipe(
        take(1),
        tap(organization => {
          this.isFurtherQualificationEligible = [QualificationLevel.LEVEL_1, QualificationLevel.LEVEL_2].includes(
            organization.qualificationLevel as QualificationLevel
          );
          this.isFurtherQualificationProcessInProgress = !!organization.openFurtherQualificationProcess;
        }),
        catchError(error => {
          console.error('Error fetching organization:', error);
          return of(null);
        })
      );

      const bookedVariants$ = this.bookingService
        .getBookingsForUser([
          BookingState.BOOKED,
          BookingState.ACCEPTED,
          BookingState.PENDING,
          BookingState.TO_BE_CANCELLED,
          BookingState.ENDED,
        ])
        .pipe(
          take(1),
          tap(bookings => {
            const foundVariant = bookings.result.find(variant => variant.cartridgeVariantId === this.variantId);
            if (foundVariant) {
              this.handleCartridgeVariantResponse(foundVariant);
            }
          })
        );

      const cartridgeVariantId$ = this.cartridgesFacade.cartridgeByVariantId$(this.variantId).pipe(
        take(1),
        tap(cartridgeId => {
          this.cartridgeId = cartridgeId?.id;
        })
      );
      forkJoin([organization$, bookedVariants$, roles$, cartridgeVariantId$]).subscribe();
    }
  }

  handleCartridgeVariantResponse(cartridgeVariant?: BookingData): void {
    if (!cartridgeVariant) return;
    const hasValidEndDate = !!cartridgeVariant?.usagePeriodEndDate;
    const isValidStatus = this.validStatus.includes(cartridgeVariant.bookingState);

    if (!hasValidEndDate && !isValidStatus) {
      return;
    }

    this.variantStatus = cartridgeVariant.bookingState;
    this.daysRemaining = daysLeft(new Date(cartridgeVariant.usagePeriodEndDate as string), this.now);
    this.statusDemo = this.getChipColorScheme(this.daysRemaining, this.variantStatus);
    this.labelTranslationKey = this.getChipLabelTranslationKey(this.daysRemaining, this.variantStatus);
  }

  async goToServiceCatalog(): Promise<void> {
    await this.router.navigateByUrl(`/cartridge/catalog/${this.cartridgeId}/detail`);
  }

  getChipColorScheme(daysLeft: number | null, status: BookingState): ChipColorScheme {
    const demoIsOver = daysLeft == null || daysLeft < 0;
    const isEndingSoon = !demoIsOver && daysLeft !== null && daysLeft <= 2;
    const isStatusEnded = this.variantStatusEnded.includes(status);

    if (isEndingSoon && !isStatusEnded) {
      return 'warning';
    }

    if (demoIsOver || isStatusEnded) {
      return 'neutral';
    }

    return 'secondary';
  }
}
